import { useState, useRef, useEffect } from 'react';
import { Plus, Link2, AlertTriangle, X, Lock, Pencil } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { InfluencerInvoice, MohitInvoice } from '../types/campaign';
import { formatEuro } from '../utils/currency';
import { commissionShare } from '../utils/commissionCalculations';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import type { InvoiceLink } from '../services/invoiceLinkService';
import type { PayableReadiness } from '../utils/payableReadiness';
import { RowList, ExpandableRow, type RowTone } from './ui/ListRows';
import { FormBlock } from './ui/FormBlock';

export interface AvailableInvoiceName {
  name: string;
  type: 'influencer' | 'service';
  suggestedAmount: number;
  sourceId?: string;
}

interface InfluencerInvoicesProps {
  invoices: InfluencerInvoice[];
  onChange: (invoices: InfluencerInvoice[]) => void;
  availableNames?: AvailableInvoiceName[];
  netFeeInfluencer: number;
  errorIds?: Set<string>;
  totalMismatch?: { expected: number; actual: number } | null;
  clientInvoices?: MohitInvoice[];
  invoiceLinks?: InvoiceLink[];
  onCreateLink?: (payableId: string, clientId: string) => void;
  onRemoveLink?: (payableId: string) => void;
  /** Removes one specific pairing; enables several receipts per payable. */
  onRemoveLinkPair?: (payableId: string, clientId: string) => void;
  /** Allow linking invoices that live in the campaign JSON (ids start with temp-). */
  allowUnsavedClientLinks?: boolean;
  requireLink?: boolean;
  linkWarningIds?: Set<string>;
  expectedNetByClientInvoice?: Record<string, Record<string, number>>;
  totalCommission?: number;
  defaultSourceType?: 'influencer' | 'service';
  hideWhenEmpty?: boolean;
  providerMode?: boolean;
  /** Derived per payable, in list order: paid / ready to pay / waiting for the client */
  readiness?: PayableReadiness[];
  /** Outstanding deductions that come off the next transfer */
  deductionsToWithhold?: number;
}

/** A payable whose invoice has arrived keeps its document data locked. */
export const isPayableLocked = (invoice: InfluencerInvoice) =>
  !!invoice.invoice_number && (invoice.status === 'Received' || invoice.status === 'Paid');

interface SortableInvoiceItemProps {
  invoice: InfluencerInvoice;
  index: number;
  isExpanded: boolean;
  isOnlyInvoice: boolean;
  availableNames: AvailableInvoiceName[];
  trancheAmount: number;
  totalAmount: number;
  hasError?: boolean;
  isManual: boolean;
  clientInvoices?: MohitInvoice[];
  linkedClientIds?: string[];
  allowUnsavedClientLinks?: boolean;
  onLinkChange?: (payableId: string, clientId: string) => void;
  onUnlink?: (payableId: string) => void;
  onUnlinkPair?: (payableId: string, clientId: string) => void;
  onToggleExpand: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, field: string, value: string | number) => void;
  onAmountChange: (id: string, value: number) => void;
  onSelectName: (invoiceId: string, name: string, suggestedAmount: number, type: 'influencer' | 'service', sourceId?: string) => void;
  onSetQuickDueDate: (invoiceId: string, days: number) => void;
  t: (key: string) => string;
  ts: (key: string) => string;
  requireLink?: boolean;
  hasLinkWarning?: boolean;
  expectedNet?: number;
  /** This payable's proportional part of the campaign commission. */
  commissionHint?: { value: number; share: number; total: number } | null;
  readiness?: PayableReadiness;
}

function SortableInvoiceItem({
  invoice,
  index,
  isExpanded,
  isOnlyInvoice,
  availableNames,
  trancheAmount,
  totalAmount,
  hasError,
  isManual,
  clientInvoices,
  linkedClientIds,
  allowUnsavedClientLinks,
  onLinkChange,
  onUnlink,
  onUnlinkPair,
  onToggleExpand,
  onDelete,
  onUpdate,
  onAmountChange,
  onSelectName,
  onSetQuickDueDate,
  t,
  requireLink,
  hasLinkWarning,
  expectedNet,
  commissionHint,
  readiness,
}: SortableInvoiceItemProps) {
  // A received invoice shows its document data read-only until "Editar"
  const [unlocked, setUnlocked] = useState(false);
  // Paga / Pronta a pagar / Aguarda cliente — derived, never picked by hand
  const payState: PayableReadiness | 'open' = invoice.status === 'Paid' ? 'paid' : readiness ?? 'open';
  const payStateLabel = {
    paid: t('payable.state.paid'),
    ready: t('payable.state.ready'),
    waiting: t('payable.state.waiting'),
    open: t('payable.state.open'),
  }[payState];
  const payStateClass = {
    paid: 'text-green-700',
    ready: 'px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold',
    waiting: 'text-gray-500',
    open: 'text-gray-600',
  }[payState];
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: invoice.id });

  const [showDropdown, setShowDropdown] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredNames = availableNames.filter(item =>
    item.name.toLowerCase().includes(nameFilter.toLowerCase())
  );

  const influencerOptions = filteredNames.filter(item => item.type === 'influencer');
  const serviceOptions = filteredNames.filter(item => item.type === 'service');

  const handleNameInputChange = (value: string) => {
    setNameFilter(value);
    onUpdate(invoice.id, 'source_name', value);
    setShowDropdown(true);
  };

  const handleSelectOption = (item: AvailableInvoiceName) => {
    onSelectName(invoice.id, item.name, item.suggestedAmount, item.type, item.sourceId);
    setNameFilter('');
    setShowDropdown(false);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const locked = isPayableLocked(invoice) && !unlocked;
  const day = (iso: string | undefined) => iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-PT') : '';
  const summary = [
    invoice.issue_date && t('mohitInvoice.issuedOn').replace('{0}', day(invoice.issue_date)),
    invoice.due_date && t('mohitInvoice.dueOn').replace('{0}', day(invoice.due_date)),
  ].filter(Boolean).join(' · ');

  // Compact field: label above, input (or the locked value) below
  const field = (label: React.ReactNode, editor: React.ReactNode, value: string, isLocked = locked, className = '') => (
    <div className={`min-w-0 ${className}`}>
      <div className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">{label}</div>
      {isLocked
        ? <div className="cm-readonly !h-9 truncate" title={value || undefined}>{value || '—'}</div>
        : editor}
    </div>
  );

  // Green when paid, amber when it can be paid now, grey otherwise
  const tone: RowTone = payState === 'paid' ? 'success' : payState === 'ready' ? 'warning' : 'neutral';

  return (
    <ExpandableRow
      nodeRef={setNodeRef}
      style={style}
      isDragging={isDragging}
      dragHandle={isOnlyInvoice ? null : { ...attributes, ...listeners }}
      tone={tone}
      isExpanded={isExpanded}
      onToggle={() => onToggleExpand(invoice.id)}
      hasError={hasError}
      onDelete={isOnlyInvoice ? undefined : () => onDelete(invoice.id)}
      deleteLabel={t('mohitInvoice.delete')}
      aside={invoice.amount > 0 ? (
        commissionHint ? (
          <span className="flex flex-col items-end leading-tight">
            <span>{formatEuro(invoice.amount)}</span>
            <span className="text-[11px] font-medium text-emerald-700" title={t('mohitInvoice.commissionHintTitle')}>
              {t('mohitInvoice.commissionShort').replace('{0}', formatEuro(commissionHint.value))}
            </span>
          </span>
        ) : formatEuro(invoice.amount)
      ) : ''}
      summary={<>
        <span className="text-sm font-semibold text-gray-900 truncate max-w-[220px]">
          {invoice.source_name || t('mohitInvoice.invoiceNumber').replace('{0}', String(index + 1))}
        </span>
        {invoice.invoice_number && (
          <span className="text-sm text-gray-600 whitespace-nowrap">Nº {invoice.invoice_number}</span>
        )}
        <span className={`text-xs font-medium whitespace-nowrap ${payStateClass}`}>
          {payStateLabel}
        </span>
        {invoice.status === 'Received' && (
          <span className="text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">
            {t('payable.invoiceReceived')}
          </span>
        )}
        {summary && <span className="hidden md:inline text-xs text-gray-500 truncate min-w-0">{summary}</span>}
      </>}
    >
          {isPayableLocked(invoice) && (
            <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" />
                {locked ? t('payable.lockedHint') : t('mohitInvoice.editingHint')}
              </span>
              <button
                type="button"
                onClick={() => setUnlocked(u => !u)}
                className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline"
              >
                {locked ? <><Pencil className="w-3.5 h-3.5" /> {t('mohitInvoice.edit')}</> : <><Lock className="w-3.5 h-3.5" /> {t('mohitInvoice.lock')}</>}
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2.5">
            {field(
              <>{t('payable.short.name')} <span className="text-red-500">*</span></>,
              <div className="relative" ref={dropdownRef}>
                <input
                  ref={inputRef}
                  type="text"
                  value={invoice.source_name || ''}
                  onChange={(e) => handleNameInputChange(e.target.value)}
                  onFocus={() => setShowDropdown(true)}
                  className="cm-input !h-9"
                  placeholder="Selecionar ou digitar nome..."
                />
                {showDropdown && (influencerOptions.length > 0 || serviceOptions.length > 0) && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {influencerOptions.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                          Influenciadores
                        </div>
                        {influencerOptions.map((item) => (
                          <button
                            key={`inf-${item.name}`}
                            type="button"
                            onClick={() => handleSelectOption(item)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-accent-50 flex justify-between items-center"
                          >
                            <span>{item.name}</span>
                            <span className="text-xs text-gray-500">{formatEuro(item.suggestedAmount)}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {serviceOptions.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                          Serviços
                        </div>
                        {serviceOptions.map((item) => (
                          <button
                            key={`svc-${item.name}`}
                            type="button"
                            onClick={() => handleSelectOption(item)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-accent-50 flex justify-between items-center"
                          >
                            <span>{item.name}</span>
                            <span className="text-xs text-gray-500">{formatEuro(item.suggestedAmount)}</span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>,
              invoice.source_name || '',
              locked,
              'col-span-2'
            )}
            {field(
              t('mohitInvoice.short.number'),
              <input
                type="text"
                value={invoice.invoice_number}
                onChange={(e) => onUpdate(invoice.id, 'invoice_number', e.target.value)}
                className="cm-input !h-9"
                placeholder="Nº da fatura do fornecedor"
              />,
              invoice.invoice_number
            )}
            {field(
              <>
                {t('mohitInvoice.short.amount')}
                {invoice.status === 'Paid' && <span className="text-red-500">*</span>}
                {totalAmount > 0 && invoice.amount > 0 && (
                  <span className="font-normal text-gray-400">{((invoice.amount / totalAmount) * 100).toFixed(0)}%</span>
                )}
                {isManual && !locked && <span className="font-normal text-gray-400">manual</span>}
              </>,
              <input
                type="number"
                value={invoice.amount || ''}
                onChange={(e) => onAmountChange(invoice.id, parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                className={`cm-input cm-numeric !h-9 ${
                  hasError
                    ? 'border-red-500 ring-2 ring-red-200 focus-visible:ring-red-500 focus-visible:border-red-500'
                    : ''
                }`}
                placeholder={trancheAmount > 0 ? String(trancheAmount) : '0.00'}
              />,
              invoice.amount > 0 ? formatEuro(invoice.amount) : ''
            )}
            {field(
              t('mohitInvoice.short.issued'),
              <input
                type="date"
                value={invoice.issue_date || ''}
                onChange={(e) => onUpdate(invoice.id, 'issue_date', e.target.value)}
                className="cm-date !h-9"
              />,
              day(invoice.issue_date)
            )}
            {field(
              <>
                {t('mohitInvoice.short.due')}
                {!locked && [30, 60, 90].map(days => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => onSetQuickDueDate(invoice.id, days)}
                    className="px-1.5 rounded-full border border-gray-300 text-[10px] font-semibold text-gray-600 hover:border-brand-ink hover:text-brand-ink"
                    title={t(`mohitInvoice.dueDatePreset${days}`)}
                  >
                    {days}
                  </button>
                ))}
              </>,
              <input
                type="date"
                value={invoice.due_date || ''}
                onChange={(e) => onUpdate(invoice.id, 'due_date', e.target.value)}
                className="cm-date !h-9"
              />,
              day(invoice.due_date)
            )}
            {/* Payment stays editable on a locked invoice */}
            {field(
              <>{t('payable.paymentLabel')} <span className="text-red-500">*</span></>,
              <select
                value={invoice.status === 'Paid' ? 'Paid' : 'Open'}
                onChange={(e) => onUpdate(
                  invoice.id,
                  'status',
                  e.target.value === 'Paid' ? 'Paid' : invoice.status === 'Received' ? 'Received' : 'Not Requested'
                )}
                className="cm-select !h-9"
              >
                <option value="Open">{t('payable.state.open')}</option>
                <option value="Paid">{t('payable.state.paid')}</option>
              </select>,
              '',
              false
            )}
            {invoice.status === 'Paid' ? field(
              t('influencerInvoice.paymentDate'),
              <input
                type="date"
                value={invoice.payment_received_date || ''}
                onChange={(e) => onUpdate(invoice.id, 'payment_received_date', e.target.value)}
                className="cm-date !h-9"
              />,
              '',
              false
            ) : (
              <label className="flex items-center gap-2 self-end h-9 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={invoice.status === 'Received'}
                  onChange={(e) => onUpdate(invoice.id, 'status', e.target.checked ? 'Received' : 'Not Requested')}
                />
                {t('payable.invoiceReceivedCheckbox')}
              </label>
            )}
          </div>

          {/* Hints, one short line each */}
          {hasError && (
            <p className="text-xs text-red-600 font-medium">Preencha o valor pago (deve ser maior que 0)</p>
          )}
          {trancheAmount > 0 && (!invoice.amount || invoice.amount === 0) && (
            <p className="text-xs text-gray-500">Sugerido: {formatEuro(trancheAmount)}</p>
          )}
          {expectedNet != null && expectedNet > 0 && (linkedClientIds?.length ?? 0) > 0 && (
            invoice.amount > 0 ? (
              Math.abs(invoice.amount - expectedNet) < 0.01 ? (
                <p className="text-xs text-emerald-600 font-medium">OK — corresponde ao esperado</p>
              ) : (
                <p className="text-xs text-amber-600 font-medium">
                  Diferença: {formatEuro(invoice.amount - expectedNet)} (esperado: {formatEuro(expectedNet)})
                </p>
              )
            ) : (
              <p className="text-xs text-blue-600">Valor esperado: {formatEuro(expectedNet)}</p>
            )
          )}
          {invoice.status !== 'Paid' && readiness === 'ready' && (
            <p className="text-xs text-emerald-700 font-medium">{t('payable.hint.ready')}</p>
          )}
          {invoice.status !== 'Paid' && readiness === 'waiting' && (
            <p className="text-xs text-gray-500">{t('payable.hint.waiting')}</p>
          )}
          {commissionHint && (
            <p className="text-xs text-emerald-700">
              {t('mohitInvoice.commissionHint')
                .replace('{0}', formatEuro(commissionHint.value))
                .replace('{1}', new Intl.NumberFormat('pt-PT', { style: 'percent', maximumFractionDigits: 1 }).format(commissionHint.share))
                .replace('{2}', formatEuro(commissionHint.total))}
            </p>
          )}


          {/* Which received invoice(s) fund this payable */}
          {clientInvoices && clientInvoices.length > 0 && onLinkChange && (() => {
            const linkedIds = linkedClientIds ?? [];
            const selectable = clientInvoices.filter(
              c => allowUnsavedClientLinks || !c.id.startsWith('temp-')
            );
            const label = (c: MohitInvoice) => {
              const i = clientInvoices.findIndex(x => x.id === c.id);
              return `${c.invoice_number ? `N.º ${c.invoice_number}` : `${t('influencerInvoice.trancheWord')} ${i + 1}`} — ${formatEuro(c.amount_received || 0)}`;
            };
            const linked = linkedIds
              .map(id => clientInvoices.find(c => c.id === id))
              .filter((c): c is MohitInvoice => !!c);
            const available = selectable.filter(c => !linkedIds.includes(c.id));

            return (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
                  <Link2 className="w-3.5 h-3.5 text-accent-600" />
                  {t('influencerInvoice.fundedBy')}
                  {requireLink && <span className="text-red-500 ml-0.5">*</span>}
                </label>

                <div className="flex flex-wrap items-center gap-1.5">
                  {linked.map((c) => (
                    <span
                      key={c.id}
                      className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-[11px] font-medium rounded-full border ${
                        c.status === 'Paid'
                          ? 'bg-green-50 text-green-800 border-green-200'
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'Paid' ? 'bg-green-500' : 'bg-gray-300'}`} />
                      {label(c)}
                      <button
                        type="button"
                        onClick={() => (onUnlinkPair ? onUnlinkPair(invoice.id, c.id) : onUnlink?.(invoice.id))}
                        className="p-0.5 rounded-full hover:bg-black/10 transition-colors"
                        title={t('influencerInvoice.removeLink')}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}

                  {available.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) onLinkChange(invoice.id, e.target.value); }}
                      className={`cm-select text-[11px] py-0.5 pl-2 pr-6 w-auto max-w-[240px] ${
                        hasLinkWarning && linked.length === 0 ? 'border-amber-400 ring-1 ring-amber-200' : ''
                      }`}
                    >
                      <option value="">{t('influencerInvoice.addLink')}</option>
                      {available.map((c) => (
                        <option key={c.id} value={c.id}>
                          {label(c)}{c.status === 'Paid' ? ' · Paga' : ` · ${c.status}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {hasLinkWarning && linked.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {t('influencerInvoice.noLinkWarning')}
                  </p>
                )}
              </div>
            );
          })()}
    </ExpandableRow>
  );
}

export function InfluencerInvoices({ invoices, onChange, availableNames = [], netFeeInfluencer, errorIds, totalMismatch, clientInvoices, invoiceLinks, onCreateLink, onRemoveLink, onRemoveLinkPair, allowUnsavedClientLinks, requireLink, linkWarningIds, expectedNetByClientInvoice, totalCommission, defaultSourceType, hideWhenEmpty, providerMode, readiness, deductionsToWithhold = 0 }: InfluencerInvoicesProps) {
  const { t, ts } = useLanguage();
  const { toast } = useModal();
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(
    new Set(invoices.length === 1 && !isPayableLocked(invoices[0]) ? [invoices[0]?.id] : [])
  );
  const [numberOfTranches, setNumberOfTranches] = useState(() => Math.max(1, invoices.length));
  const [manualAmountIds, setManualAmountIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    invoices.forEach(inv => {
      if ((inv.amount || 0) > 0) initial.add(inv.id);
    });
    return initial;
  });
  const autoPopulatedRef = useRef<Set<string>>(new Set());

  // With several payables, each shows the part of the commission that
  // matches its share of what the creators/providers get (it used to be an
  // equal split, wrong as soon as the amounts differ)
  const payableBase = netFeeInfluencer > 0
    ? netFeeInfluencer
    : invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const commissionHintFor = (invoice: InfluencerInvoice) => {
    if (invoices.length < 2 || !totalCommission) return null;
    const share = commissionShare(invoice.amount || 0, payableBase, totalCommission);
    return share ? { ...share, total: totalCommission } : null;
  };

  useEffect(() => {
    if (errorIds && errorIds.size > 0) {
      setExpandedInvoices(prev => {
        const next = new Set(prev);
        errorIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [errorIds]);

  const computePerTranche = (total: number, count: number) =>
    total > 0 && count >= 1 ? Math.round((total / count) * 100) / 100 : 0;

  const trancheAmount = computePerTranche(netFeeInfluencer, numberOfTranches);

  const redistributeAmounts = (
    list: InfluencerInvoice[],
    _count: number,
    totalOverride?: number,
    manualOverrideIds?: Set<string>
  ): InfluencerInvoice[] => {
    const baseTotal = totalOverride ?? netFeeInfluencer;
    const manualIds = manualOverrideIds ?? manualAmountIds;
    const lockedIndexes = new Set<number>();
    list.forEach((inv, idx) => {
      if (inv.status === 'Paid' || manualIds.has(inv.id)) lockedIndexes.add(idx);
    });
    const flexibleIndexes: number[] = [];
    list.forEach((_, idx) => {
      if (!lockedIndexes.has(idx)) flexibleIndexes.push(idx);
    });
    if (flexibleIndexes.length === 0) return list;
    const lockedSum = list.reduce(
      (sum, inv, idx) => (lockedIndexes.has(idx) ? sum + (inv.amount || 0) : sum),
      0
    );
    const remaining = Math.max(0, Math.round((baseTotal - lockedSum) * 100) / 100);
    if (remaining === 0) {
      return list.map((inv, idx) =>
        lockedIndexes.has(idx) ? inv : { ...inv, amount: 0 }
      );
    }
    const evenShare = Math.round((remaining / flexibleIndexes.length) * 100) / 100;
    const assigned = evenShare * flexibleIndexes.length;
    const remainder = Math.round((remaining - assigned) * 100) / 100;
    const lastFlexible = flexibleIndexes[flexibleIndexes.length - 1];
    return list.map((inv, idx) => {
      if (lockedIndexes.has(idx)) return inv;
      const value = idx === lastFlexible ? Math.round((evenShare + remainder) * 100) / 100 : evenShare;
      return { ...inv, amount: value };
    });
  };

  const lastNetFeeRef = useRef<number>(netFeeInfluencer);
  useEffect(() => {
    if (lastNetFeeRef.current === netFeeInfluencer) return;
    lastNetFeeRef.current = netFeeInfluencer;
    if (invoices.length === 0) return;
    const hasNonPaid = invoices.some(inv => inv.status !== 'Paid');
    if (!hasNonPaid) return;
    const updated = redistributeAmounts(invoices, numberOfTranches, netFeeInfluencer);
    const changed = updated.some((inv, i) => inv.amount !== invoices[i].amount);
    if (changed) onChange(updated);
  }, [netFeeInfluencer]);

  useEffect(() => {
    if (availableNames.length !== 1) return;
    const auto = availableNames[0];
    const needsUpdate = invoices.some(
      inv => !inv.source_name && inv.amount === 0 && !autoPopulatedRef.current.has(inv.id)
    );
    if (!needsUpdate) return;

    const perTranche = computePerTranche(netFeeInfluencer, invoices.length);
    const updated = invoices.map(inv => {
      if (!inv.source_name && inv.amount === 0 && !autoPopulatedRef.current.has(inv.id)) {
        autoPopulatedRef.current.add(inv.id);
        return {
          ...inv,
          source_name: auto.name,
          source_type: auto.type,
          amount: perTranche,
        };
      }
      return inv;
    });
    onChange(updated);
  }, [availableNames]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const computeRemainingInfluencerAmount = () => {
    const allocated = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    const diff = Math.round((netFeeInfluencer - allocated) * 100) / 100;
    return diff > 0 ? diff : 0;
  };

  const addInvoice = () => {
    const autoFill = availableNames.length === 1 ? availableNames[0] : null;
    const newCount = invoices.length + 1;
    const newInvoice: InfluencerInvoice = {
      id: `temp-${crypto.randomUUID()}`,
      invoice_number: '',
      issue_date: '',
      due_date: '',
      amount: computeRemainingInfluencerAmount(),
      status: 'Not Requested',
      payment_received_date: '',
      source_name: autoFill?.name || '',
      source_type: autoFill?.type || defaultSourceType || undefined,
    };
    setNumberOfTranches(newCount);
    onChange([...invoices, newInvoice]);
    setExpandedInvoices(new Set([newInvoice.id]));
  };

  const addTranche = () => {
    const autoFill = availableNames.length === 1 ? availableNames[0] : null;
    const newCount = numberOfTranches + 1;
    const newInvoice: InfluencerInvoice = {
      id: `temp-${crypto.randomUUID()}`,
      invoice_number: '',
      issue_date: '',
      due_date: '',
      amount: computeRemainingInfluencerAmount(),
      status: 'Not Requested',
      payment_received_date: '',
      source_name: autoFill?.name || '',
      source_type: autoFill?.type || defaultSourceType || undefined,
    };
    setNumberOfTranches(newCount);
    onChange([...invoices, newInvoice]);
    setExpandedInvoices(new Set([newInvoice.id]));
  };

  const removeTranche = () => {
    if (invoices.length <= 1) return;
    const newCount = Math.max(1, numberOfTranches - 1);
    const sliced = invoices.slice(0, -1);
    const updated = redistributeAmounts(sliced, newCount);
    setNumberOfTranches(newCount);
    onChange(updated);
  };

  const updateInvoice = (id: string, field: string, value: string | number) => {
    const updated = invoices.map(inv => {
      if (inv.id !== id) return inv;
      const patch: Partial<InfluencerInvoice> = { [field]: value };
      if (field === 'status' && value === 'Paid') {
        if (!inv.amount || inv.amount === 0) {
          patch.amount = trancheAmount;
        }
      }
      if (field === 'source_name' && !inv.source_type && defaultSourceType) {
        patch.source_type = defaultSourceType;
      }
      return { ...inv, ...patch };
    });
    onChange(updated);
  };

  const selectName = (invoiceId: string, name: string, suggestedAmount: number, type: 'influencer' | 'service', sourceId?: string) => {
    const amount = suggestedAmount > 0 ? suggestedAmount : computePerTranche(netFeeInfluencer, invoices.length);
    const updated = invoices.map(inv =>
      inv.id === invoiceId
        ? { ...inv, source_name: name, source_type: type, source_id: sourceId, amount: inv.status === 'Paid' ? inv.amount : amount }
        : inv
    );
    onChange(updated);
  };

  const deleteInvoice = (id: string) => {
    if (invoices.length === 1) return;
    const filtered = invoices.filter(inv => inv.id !== id);
    const nextManual = new Set(manualAmountIds);
    nextManual.delete(id);
    setManualAmountIds(nextManual);
    const newCount = Math.max(1, numberOfTranches - 1);
    const updated = redistributeAmounts(filtered, newCount, netFeeInfluencer, nextManual);
    setNumberOfTranches(newCount);
    onChange(updated);
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleAmountChange = (id: string, value: number) => {
    setManualAmountIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const updated = invoices.map(inv =>
      inv.id === id ? { ...inv, amount: value } : inv
    );
    onChange(updated);
  };

  const distributeEvenly = () => {
    setManualAmountIds(new Set());
    const uniqueNames = new Set(invoices.map(inv => inv.source_name).filter(Boolean));
    if (uniqueNames.size > 1) {
      // Multi-creator: even-split within each creator's group
      const updated = invoices.map(inv => {
        if (inv.status === 'Paid') return inv;
        const groupInvoices = invoices.filter(i => i.source_name === inv.source_name && i.status !== 'Paid');
        const creatorNet = availableNames.find(n => n.name === inv.source_name)?.suggestedAmount ?? 0;
        const share = groupInvoices.length > 0 ? Math.round((creatorNet / groupInvoices.length) * 100) / 100 : 0;
        return { ...inv, amount: share };
      });
      onChange(updated);
    } else {
      // Single-creator or unnamed: even-split of total
      const updated = redistributeAmounts(invoices, invoices.length, netFeeInfluencer, new Set());
      onChange(updated);
    }
  };

  const distributeByCreatorNet = () => {
    setManualAmountIds(new Set());
    const updated = invoices.map(inv => {
      if (inv.status === 'Paid') return inv;
      const match = availableNames.find(n => n.name === inv.source_name);
      const creatorNet = match?.suggestedAmount ?? 0;
      // If multiple invoices share the same creator, split that creator's net among them
      const sameCreatorInvoices = invoices.filter(i => i.source_name === inv.source_name && i.status !== 'Paid');
      const share = sameCreatorInvoices.length > 0 ? Math.round((creatorNet / sameCreatorInvoices.length) * 100) / 100 : creatorNet;
      return { ...inv, amount: share };
    });
    onChange(updated);
  };

  const distributeRemaining = () => {
    const updated = redistributeAmounts(invoices, invoices.length, netFeeInfluencer);
    onChange(updated);
  };

  const toggleExpand = (id: string) => {
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const calculateDueDate = (fromDate: string, days: number): string => {
    const date = new Date(fromDate);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const setQuickDueDate = (invoiceId: string, days: number) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) return;

    if (!invoice.issue_date) {
      toast.warning(t('influencerInvoice.setIssueDateFirst'));
      return;
    }
    const dueDate = calculateDueDate(invoice.issue_date, days);
    updateInvoice(invoiceId, 'due_date', dueDate);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = invoices.findIndex(inv => inv.id === active.id);
      const newIndex = invoices.findIndex(inv => inv.id === over.id);
      const reordered = arrayMove(invoices, oldIndex, newIndex);
      onChange(reordered);
    }
  };

  // "Pago X de Y": what has actually gone out vs the whole plan
  const totalPlanned = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalAmountPaid = invoices
    .filter(inv => inv.status === 'Paid')
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalReady = invoices
    .filter((inv, i) => inv.status !== 'Paid' && readiness?.[i] === 'ready')
    .reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const allPaid = invoices.length > 0 && invoices.every(inv => inv.status === 'Paid');

  const addButton = (
    <button type="button" onClick={addInvoice} className="cm-btn-add whitespace-nowrap">
      <Plus className="w-4 h-4" />
      {t('mohitInvoice.addInvoice')}
    </button>
  );

  return (
    <FormBlock title={t('payable.blockTitle')}>
    <div className="space-y-3">
      {/* With a tranche bar the button sits at its end; otherwise on its own */}
      {!(netFeeInfluencer > 0 && !(hideWhenEmpty && invoices.length === 0)) && (
        <div className="flex flex-wrap items-center justify-end gap-2">{addButton}</div>
      )}

      {!(hideWhenEmpty && invoices.length === 0) && (<>

      {netFeeInfluencer > 0 && (() => {
        const allocated = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
        const diff = Math.round((netFeeInfluencer - allocated) * 100) / 100;
        const balanceTone = diff === 0
          ? 'text-emerald-700'
          : diff > 0
            ? 'text-amber-700'
            : 'text-red-700';
        const balanceLabel = diff === 0
          ? 'Totalmente alocado'
          : diff > 0
            ? `Restante: ${formatEuro(diff)}`
            : `Excedido: ${formatEuro(Math.abs(diff))}`;
        return (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-1">
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap">
              {t('mohitInvoice.tranches')}
            </label>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={removeTranche}
                disabled={invoices.length <= 1}
                className="w-6 h-6 flex items-center justify-center text-sm font-medium bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                -
              </button>
              <span className="w-8 text-center text-sm font-semibold text-gray-900">
                {numberOfTranches}
              </span>
              <button
                type="button"
                onClick={addTranche}
                className="w-6 h-6 flex items-center justify-center text-sm font-medium bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
              >
                +
              </button>
            </div>
            <span className="text-xs text-gray-500">|</span>
            <button
              type="button"
              onClick={distributeEvenly}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline transition-colors"
              title="Distribuir uniformemente, ignorando valores manuais"
            >
              Distribuir uniformemente
            </button>
            {availableNames.length > 1 && (
              <button
                type="button"
                onClick={distributeByCreatorNet}
                className="text-xs font-medium text-accent-700 hover:text-accent-900 underline-offset-2 hover:underline transition-colors"
                title="Atribuir a cada fatura o valor liquido do criador correspondente"
              >
                Distribuir por liquido
              </button>
            )}
            <button
              type="button"
              onClick={distributeRemaining}
              disabled={diff === 0}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline transition-colors disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
              title="Distribuir o restante pelos valores não manuais"
            >
              Distribuir restante
            </button>
            <span className="text-xs text-gray-500">|</span>
            <span className="text-xs text-gray-600">
              {formatEuro(allocated)} / {formatEuro(netFeeInfluencer)}
            </span>
            <span className={`text-xs font-medium ${balanceTone}`}>
              {balanceLabel}
            </span>
            <span className="ml-auto">{addButton}</span>
          </div>
        );
      })()}

      {totalMismatch && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-xs text-red-800">
          <p className="font-semibold mb-0.5">O total pago nao corresponde ao valor liquido da campanha</p>
          <p>
            Esperado:{' '}
            <span className="font-semibold">{formatEuro(totalMismatch.expected)}</span>
            {' | '}
            Introduzido:{' '}
            <span className="font-semibold">{formatEuro(totalMismatch.actual)}</span>
          </p>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={invoices.map(inv => inv.id)}
          strategy={verticalListSortingStrategy}
        >
          {invoices.length > 0 && <RowList>
            {invoices.map((invoice, index) => {
              const isExpanded = expandedInvoices.has(invoice.id);
              const isOnlyInvoice = invoices.length === 1;

              const linkedClientIds = (invoiceLinks ?? [])
                .filter(l => l.payable_invoice_id === invoice.id)
                .map(l => l.client_invoice_id);
              const linkedClientId = linkedClientIds[0];

              return (
                <SortableInvoiceItem
                  key={invoice.id}
                  invoice={invoice}
                  index={index}
                  isExpanded={isExpanded}
                  isOnlyInvoice={isOnlyInvoice}
                  availableNames={availableNames}
                  trancheAmount={trancheAmount}
                  totalAmount={netFeeInfluencer}
                  hasError={errorIds?.has(invoice.id)}
                  isManual={manualAmountIds.has(invoice.id)}
                  clientInvoices={clientInvoices}
                  linkedClientIds={linkedClientIds}
                  allowUnsavedClientLinks={allowUnsavedClientLinks}
                  onLinkChange={onCreateLink}
                  onUnlink={onRemoveLink}
                  onUnlinkPair={onRemoveLinkPair}
                  onToggleExpand={toggleExpand}
                  onDelete={deleteInvoice}
                  onUpdate={updateInvoice}
                  onAmountChange={handleAmountChange}
                  onSelectName={selectName}
                  onSetQuickDueDate={setQuickDueDate}
                  t={t}
                  ts={ts}
                  requireLink={requireLink}
                  hasLinkWarning={linkWarningIds?.has(invoice.id)}
                  expectedNet={linkedClientId && expectedNetByClientInvoice ? expectedNetByClientInvoice[linkedClientId]?.[invoice.source_name || ''] : undefined}
                  commissionHint={commissionHintFor(invoice)}
                  readiness={readiness?.[index]}
                />
              );
            })}
          </RowList>}
        </SortableContext>
      </DndContext>

      <div className="flex items-center justify-between px-1">
        <span className="text-sm font-medium text-gray-700">{providerMode ? t('payable.footer.paidToProvider') : t('payable.footer.paidToCreator')}</span>
        <span className="text-right">
          <span className={`text-sm font-bold ${allPaid ? 'text-green-600' : 'text-gray-900'}`}>
            {t('payable.footer.paidOf')
              .replace('{0}', formatEuro(totalAmountPaid))
              .replace('{1}', formatEuro(totalPlanned))}
            {allPaid && <span className="ml-2 text-xs font-normal">{t('influencerInvoice.allPaid')}</span>}
          </span>
          {totalReady > 0 && (
            <span className="block text-xs font-semibold text-emerald-700">
              {deductionsToWithhold > 0.005
                ? t('payable.footer.readyAfterDeductions')
                    .replace('{0}', formatEuro(totalReady))
                    .replace('{1}', formatEuro(Math.min(deductionsToWithhold, totalReady)))
                    .replace('{2}', formatEuro(Math.max(0, totalReady - deductionsToWithhold)))
                : t('payable.footer.ready').replace('{0}', formatEuro(totalReady))}
            </span>
          )}
        </span>
      </div>

      </>)}
    </div>
    </FormBlock>
  );
}
